<template>
  <div>
    <h4>Appearance</h4>
    <sme-card large>
      <div class="card-container">
        <div class="label-row">
          <div class="label">
            Card Design
            <small>
              Customise how this award looks to match your brand, or choose from our pre-designed templates.
            </small>
          </div>
        </div>

        <div class="card-design-columns">
          <div class="card-design-uploads">
            <div class="image-upload-container">
              <div class="label">Banner image</div>

              <div class="image-upload-preview-container">
                <div :class="['banner-preview', { 'banner-preview--with-image': bannerPreviewUrl }]">
                  <template v-if="bannerPreviewUrl">
                    <img :src="bannerPreviewUrl" alt="Banner preview" class="image-upload-preview-image" />
                  </template>
                  <template v-else>
                    <div class="image-upload-preview-placeholder">
                      <font-awesome-icon :icon="['far', 'image']" size="2xl" />
                    </div>
                  </template>
                </div>
                <div class="image-upload-actions">
                  <b-button variant="primary" v-b-modal.banner-upload-modal size="sm">
                    {{ bannerPreviewUrl ? 'Upload another image' : 'Upload image' }}
                  </b-button>
                  <span v-b-tooltip.hover="!hasBannerGalleryImages ? 'No gallery images available' : ''" tabindex="0">
                    <b-button
                      variant="outline-primary"
                      v-b-modal.banner-gallery-modal
                      size="sm"
                      :disabled="!hasBannerGalleryImages"
                      >Choose from gallery</b-button
                    >
                  </span>
                  <b-button v-if="bannerPreviewUrl" variant="outline-danger" size="sm" @click="$emit('remove:banner')"
                    >Remove</b-button
                  >
                </div>
              </div>
            </div>

            <image-upload-modal
              modal-id="banner-upload-modal"
              title="Add banner image"
              :accepted-file-types="allowedImageTypes"
              :max-size-bytes="maxSizeBytes"
              :file="bannerFile"
              :preview-url="bannerPreviewUrl"
              supported-text="JPG or PNG • up to 10 MB • Recommended 1600 × 900 px (16:9)"
              @update:file="$emit('update:bannerFile', $event)"
              @update:previewUrl="$emit('update:bannerPreviewUrl', $event)"
            />

            <image-gallery-modal
              modal-id="banner-gallery-modal"
              image-type="banner"
              @select="$emit('gallerySelect:banner', $event)"
            />

            <div class="image-upload-container">
              <div class="label">Award badge</div>

              <div class="image-upload-preview-container">
                <div class="badge-preview">
                  <template v-if="badgePreviewUrl">
                    <img :src="badgePreviewUrl" alt="Badge preview" class="image-upload-preview-image" />
                  </template>
                  <template v-else>
                    <div class="image-upload-preview-placeholder">
                      <font-awesome-icon :icon="['far', 'image']" size="2xl" />
                    </div>
                  </template>
                </div>
                <div class="image-upload-actions">
                  <b-button variant="primary" v-b-modal.badge-upload-modal size="sm">
                    {{ badgePreviewUrl ? 'Upload another image' : 'Upload image' }}
                  </b-button>
                  <span v-b-tooltip.hover="!hasBadgeGalleryImages ? 'No gallery images available' : ''" tabindex="0">
                    <b-button
                      variant="outline-primary"
                      v-b-modal.badge-gallery-modal
                      size="sm"
                      :disabled="!hasBadgeGalleryImages"
                      >Choose from gallery</b-button
                    >
                  </span>
                  <b-button v-if="badgePreviewUrl" variant="outline-danger" size="sm" @click="$emit('remove:badge')"
                    >Remove</b-button
                  >
                </div>
              </div>
            </div>

            <image-upload-modal
              modal-id="badge-upload-modal"
              title="Add badge image"
              :accepted-file-types="allowedImageTypes"
              :max-size-bytes="maxSizeBytes"
              :file="badgeFile"
              :preview-url="badgePreviewUrl"
              preview-type="badge"
              supported-text="JPG or PNG • up to 10 MB • Recommended 300 × 300 px (1:1)"
              @update:file="$emit('update:badgeFile', $event)"
              @update:previewUrl="$emit('update:badgePreviewUrl', $event)"
            />

            <image-gallery-modal
              modal-id="badge-gallery-modal"
              image-type="logo"
              @select="$emit('gallerySelect:badge', $event)"
            />
          </div>

          <div class="card-design-preview">
            <div class="card-design-preview-header">
              <div class="label">Award card preview</div>
              <small class="card-design-preview-description">See how your award card will appear to recipients.</small>
            </div>
            <div class="card-design-preview-body">
              <div class="award-card-preview">
                <div
                  :class="['award-card-preview-banner', { 'award-card-preview-banner--with-image': bannerPreviewUrl }]"
                >
                  <img
                    v-if="bannerPreviewUrl"
                    :src="bannerPreviewUrl"
                    alt="Banner"
                    class="award-card-preview-banner-image"
                  />
                  <font-awesome-icon
                    v-else
                    :icon="['far', 'image']"
                    size="2xl"
                    class="award-card-preview-placeholder-icon"
                  />
                </div>
                <div class="award-card-preview-content">
                  <div class="award-card-preview-badge">
                    <img
                      v-if="badgePreviewUrl"
                      :src="badgePreviewUrl"
                      alt="Badge"
                      class="award-card-preview-badge-image"
                    />
                    <font-awesome-icon
                      v-else
                      :icon="['far', 'image']"
                      size="lg"
                      class="award-card-preview-placeholder-icon"
                    />
                  </div>
                  <div class="award-card-preview-name">
                    {{ awardName || 'Award name' }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </sme-card>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import ApiClient from '@/ApiClient';
import SmeCard from '@/components/atoms/SmeCard.vue';
import ImageUploadModal from '@/pages/recognition/components/ImageUploadModal.vue';
import ImageGalleryModal from '@/pages/recognition/components/ImageGalleryModal.vue';

const hasBannerGalleryImages = ref(false);
const hasBadgeGalleryImages = ref(false);

onMounted(async () => {
  try {
    const data = await ApiClient.getRecognitionGalleryImages();
    hasBannerGalleryImages.value = Object.keys(data?.generic?.banner || {}).length > 0;
    hasBadgeGalleryImages.value = Object.keys(data?.generic?.logo || {}).length > 0;
  } catch {
    // leave both false — buttons stay disabled
  }
});

defineProps({
  awardName: {
    type: String,
    default: '',
  },
  bannerFile: {
    type: [Object, File],
    default: null,
  },
  bannerPreviewUrl: {
    type: String,
    default: '',
  },
  badgeFile: {
    type: [Object, File],
    default: null,
  },
  badgePreviewUrl: {
    type: String,
    default: '',
  },
  allowedImageTypes: {
    type: Array,
    default: () => ['image/jpeg', 'image/png'],
  },
  maxSizeBytes: {
    type: Number,
    default: 10 * 1024 * 1024, // 10MB
  },
});

defineEmits([
  'update:bannerFile',
  'update:bannerPreviewUrl',
  'update:badgeFile',
  'update:badgePreviewUrl',
  'gallerySelect:banner',
  'gallerySelect:badge',
  'remove:banner',
  'remove:badge',
]);
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/award-form';
</style>
